'use client'

import { useEffect, useRef } from 'react'
import { useUser } from '@/contexts/user/UserContext'
import { usePathname } from 'next/navigation'

/**
 * ProfileLoader - Efficiently loads user profile only when needed
 *
 * This component:
 * - Only loads profile if it's missing
 * - Only loads once per authId (doesn't reload on every render)
 * - Skips loading on auth pages
 * - Uses a ref to prevent duplicate loads
 */
interface ProfileLoaderProps {
  authId: string | null
}

export function ProfileLoader({ authId }: ProfileLoaderProps) {
  const { profile, fetchProfile, isLoadingProfile } = useUser()
  const pathname = usePathname()
  const loadedAuthIdRef = useRef<string | null>(null)

  // Auth pages don't need profile
  const isAuthPage =
    pathname === '/' ||
    pathname?.includes('/login') ||
    pathname?.includes('/forgot-password') ||
    pathname?.includes('/reset-password') ||
    pathname?.includes('/mfa')

  useEffect(() => {
    // Skip if on auth page
    if (isAuthPage) return

    // Skip if no authId from server
    if (!authId) return

    // Skip if already loading
    if (isLoadingProfile) return

    // Skip if profile already loaded for this user
    if (profile?.authId === authId) {
      loadedAuthIdRef.current = authId
      return
    }

    // Skip if we already loaded this authId (prevent duplicate loads)
    if (loadedAuthIdRef.current === authId) return

    // Load the profile
    console.log('[ProfileLoader] Loading profile for authId:', authId)
    loadedAuthIdRef.current = authId
    fetchProfile(authId)
  }, [authId, profile, isLoadingProfile, fetchProfile, isAuthPage])

  // This component doesn't render anything
  return null
}
